<template>
    <div class="form-signin">
        <header role="banner" class="text-center">
            <router-link to="/" class="brand-logo">
                <img :src="$i18n.locale === 'ro' ? '/img/identity/tgt_ro.png' : '/img/identity/tgt_com.png'" alt="Tapgiant">
            </router-link>
        </header>

        <div class="p-4 shadow-sm bg-white rounded">
            <div>
                <h4 class="text-gray-700 text-center">{{$t('one_last_step')}}</h4>
                <div class="text-gray-600 text-center fs-6">
                    <p>{{$t('enter_otp_instructions', {EMAIL: masked_email})}}</p>
                </div>

                <form @submit="submit" novalidate>
                    <div class="form-group mt-4 has-validation">
                        <label class="form-label" for="otp-code">{{$t('otp_code')}}</label>
                        <div class="otp-inputs" @paste="pasteCode">
                            <input v-for="(digit, index) in code_digits" :id="index === 0 ? 'otp-code' : `otp-code-${index + 1}`" :key="index" ref="otpInputs" :value="digit" :class="{'is-invalid': error_code !== -1}" :aria-label="`${$t('otp_code')} ${index + 1}`" :autocomplete="index === 0 ? 'one-time-code' : 'off'" type="text" inputmode="numeric" pattern="[0-9]" maxlength="1" class="form-control otp-input fs-3" @input="inputDigit(index, $event)" @keydown="keydownDigit(index, $event)" @focus="$event.target.select()">
                        </div>
                        <div v-if="error_code !== -1" class="invalid-feedback d-block text-center">
                            {{$t('otp_invalid_or_expired')}}
                        </div>
                    </div>

                    <div class="form-group mt-4">
                        <div class="d-grid gap-2">
                            <button :disabled="!valid_code || loading" class="btn btn-lg btn-primary btn-block fs-6 bg-teal-500 border-teal-600" type="submit">
                                {{$t('login')}}
                            </button>
                        </div>
                    </div>
                </form>

                <div class="row fs-6 pt-4">
                    <div class="col text-center">
                        <router-link to="/login" class="px-3 py-2 fs-6 text-teal-500">
                            <u><strong>{{$t('request_new_code')}}</strong></u>
                        </router-link><br>
                        <router-link to="/login" class="px-3 py-2 fs-6 text-teal-500">
                            <u><strong>{{$t('use_different_email')}}</strong></u>
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <div class="text-center pt-3">
            <router-link to="/" :title="$t('back_to_homepage')">{{$t('back_to_homepage')}}</router-link>
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue'

export default {
    data: function () {
        return {
            challenge_id: '',
            masked_email: '',
            code_digits: ['', '', '', '', '', ''],
            error_code: -1,
            loading: false
        }
    },
    computed: {
        is_authorized: function () {
            return this.$store.state.is_authorized
        },
        code: function () {
            return this.code_digits.join('')
        },
        valid_code: function () {
            return /^[0-9]{6}$/.test(this.code)
        }
    },
    created: async function () {
        if (this.is_authorized) {
            this.$router.replace('/console/account')
            return
        }

        let saved = null
        try {
            saved = JSON.parse(sessionStorage.getItem('tapgiant.login.otp'))
        } catch (error) {
            saved = null
        }

        this.challenge_id = this.$route.query.c || (saved && saved.challenge_id) || ''
        this.masked_email = this.$route.query.q || (saved && saved.masked_email) || ''

        if (!this.challenge_id) {
            this.$router.replace({ name: 'account_login_step1' }).catch(() => {})
        }
    },
    mounted: function () {
        nextTick(() => this.focusDigit(0))
    },
    methods: {
        focusDigit: function (index) {
            const inputs = this.$refs.otpInputs || []
            inputs[index]?.focus()
        },
        fillCode: function (value) {
            const digits = value.replace(/\D/g, '').slice(0, 6).split('')

            this.code_digits = Array.from({ length: 6 }, (_, index) => digits[index] || '')
            this.error_code = -1
            nextTick(() => this.focusDigit(Math.min(digits.length, 5)))
        },
        pasteCode: function ($event) {
            $event.preventDefault()
            this.fillCode($event.clipboardData.getData('text'))
        },
        inputDigit: function (index, $event) {
            const digits = $event.target.value.replace(/\D/g, '')

            if (digits.length > 1) {
                this.fillCode(digits)
                return
            }

            this.code_digits[index] = digits
            $event.target.value = digits
            this.error_code = -1

            if (digits && index < 5) {
                nextTick(() => this.focusDigit(index + 1))
            }
        },
        keydownDigit: function (index, $event) {
            if ($event.key === 'Backspace') {
                $event.preventDefault()

                if (this.code_digits[index]) {
                    this.code_digits[index] = ''
                } else if (index > 0) {
                    this.code_digits[index - 1] = ''
                    nextTick(() => this.focusDigit(index - 1))
                }
                this.error_code = -1
            } else if ($event.key === 'ArrowLeft' && index > 0) {
                $event.preventDefault()
                this.focusDigit(index - 1)
            } else if ($event.key === 'ArrowRight' && index < 5) {
                $event.preventDefault()
                this.focusDigit(index + 1)
            }
        },
        submit: async function ($event) {
            $event.preventDefault()
            if (!this.valid_code || this.loading) {
                return
            }

            this.loading = true
            const response = await this.$store.dispatch('LOGIN', {
                challenge_id: this.challenge_id,
                code: this.code
            })

            if (response.status === 0) {
                sessionStorage.removeItem('tapgiant.login.otp')
                await this.$store.dispatch('GET_INFO')
                await this.$router.replace('/console/store')
                return
            }

            this.loading = false
            this.error_code = response.error_code || response.status || 1
        }
    }
}
</script>

<style scoped>
.otp-inputs {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.5rem;
}

.otp-input {
    aspect-ratio: 1 / 1;
    min-width: 0;
    padding: 0;
    text-align: center;
}
</style>
